import { PrinterOutlined } from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { Club } from '@hefe/models';
import { ClubSlice } from '@hefe/store';
import { formatDate } from '@hefe/utils';
import { Button, Checkbox, Col, Collapse, Row, Space, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import styles from './club-data-processing.component.module.scss';

interface ClubDataProcessingComponentProps {
  club?: Club;
}

export default function ClubDataProcessingComponent(
  props: ClubDataProcessingComponentProps
) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [readAgreement, setReadAgreement] = useState(false);

  const dispatch = useAppDispatch();
  const data = useSelector(ClubSlice.dataProcessingAgreementSelector);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    dispatch(ClubSlice.fetchDataProcessingAgreement())
      .unwrap()
      .finally(() => setLoading(false));
  };

  const acceptDPA = () => {
    setSaving(true);
    dispatch(ClubSlice.agreeToDataProcessingAgreement())
      .unwrap()
      .finally(() => setSaving(false));
  };

  const handlePrint = (agreement: string) => {
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      return;
    }

    printWindow.document.open();
    printWindow.document.write(`
      <html>
      <head>
        <title>${t('data_processing.agreement')}</title>
        <style>
          @media print {
            body {
              font-size: 12pt;
              margin: 20px;
            }
          }
        </style>
      </head>
      <body>
        <div>
          <div>${agreement}</div>
        </div>
        <script>
          document.getElementById('printButton').style.display = 'block';
          document.getElementById('printButton').addEventListener('click', function() {
            window.print();
          });
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const pleaseAcceptAgreementText = useMemo(() => {
    if (
      data.all_accepted_versions.length === 0 ||
      data.accepted_version !== data.latest_version
    ) {
      return t('data_processing.no_accepted_agreements');
    }

    return '';
  }, [data]);

  const acceptedAgreementsText = useMemo(() => {
    if (data.all_accepted_versions.length === 1) {
      return t('data_processing.accepted_agreements_singular', {
        date: formatDate(data.all_accepted_versions[0].accepted_at),
      });
    } else if (data.all_accepted_versions.length > 1) {
      return t('data_processing.accepted_agreements_plural', {
        first: formatDate(
          data.all_accepted_versions[data.all_accepted_versions.length - 1]
            .accepted_at
        ),
        latest: formatDate(data.all_accepted_versions[0].accepted_at),
      });
    } else {
      return '';
    }
  }, [data]);

  const agreementsCollapse = useMemo(() => {
    const items: any = [];

    if (!data.accepted_version || data.latest_version > data.accepted_version) {
      items.push({
        key: 'data_processing_agreement',
        label: t('data_processing.agreement_with_version', {
          version: data.latest_version,
        }),
        children: (
          <div>
            <div
              className={styles['data-processing-agreement-container']}
              dangerouslySetInnerHTML={{
                __html: data.latest_data,
              }}
            />
            <Button
              onClick={() => handlePrint(data.latest_data)}
              icon={<PrinterOutlined />}
            >
              {t('print')}
            </Button>
          </div>
        ),
      });
    }

    if (data.accepted_version && data.accepted_data) {
      items.push({
        key: 'data_processing_agreement_accepted',
        label: t('data_processing.accepted_agreement_with_version', {
          version: data.accepted_version,
        }),
        children: (
          <div>
            <div
              className={styles['data-processing-agreement-container']}
              dangerouslySetInnerHTML={{
                __html: data.accepted_data,
              }}
            />
            <Button
              onClick={() => handlePrint(data.accepted_data ?? '')}
              icon={<PrinterOutlined />}
            >
              {t('print')}
            </Button>
          </div>
        ),
      });
    }

    return (
      <Collapse items={items} defaultActiveKey="data_processing_agreement" />
    );
  }, [data]);

  if (loading) {
    return <Spin />;
  }

  return (
    <Row gutter={[0, 30]}>
      <Col xs={24}>{pleaseAcceptAgreementText}</Col>
      <Col xs={24}>{acceptedAgreementsText}</Col>
      <Col xs={24}>{agreementsCollapse}</Col>
      <Col xs={24}>
        <Space size={12}>
          {(!data.accepted_version ||
            data.accepted_version > data.latest_version) && (
            <Space direction="vertical" size={20}>
              <Checkbox
                checked={readAgreement}
                onChange={(value) => setReadAgreement(value.target.checked)}
              >
                {t('data_processing.agree_disclamer_text')}
              </Checkbox>

              <Button
                disabled={!readAgreement}
                loading={saving}
                type="primary"
                onClick={() => acceptDPA()}
              >
                {t('data_processing.agree_button')}
              </Button>
            </Space>
          )}
        </Space>
      </Col>
    </Row>
  );
}
